import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Archive } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useRangeQuery } from "@/lib/useData";
import { useAuth } from "@/lib/auth";
import { useUrlFilters } from "@/lib/useUrlFilters";
import { FilterBar } from "@/components/shared/FilterBar";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { StatusBadge, ToolAvatar, RowMenu, PageHeader, SegmentedControl, DataTable } from "@/components/kit";
import { api } from "@/lib/api";
import {
  PageTitle, Card, UtilBar, StatusChip, PartialChip, MetricTip,
  LoadingBlocks, ErrorState, EmptyState, Forbidden,
} from "@/components/shared/primitives";
import { pct, money, num, shortDate, daysUntil, CATEGORY_LABELS, TRACKING_LABELS, METRIC_DEFS } from "@/lib/format";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function AddAppPanel({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", vendor: "", category: "research", domains: "",
    monthly_cost: "", billing_cycle: "monthly", renewal_date: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async () => {
    if (!form.name || !form.vendor) {
      toast.error("Add a name and vendor to continue.");
      return;
    }
    try {
      await api.post("/apps", {
        name: form.name, vendor: form.vendor, category: form.category,
        domains: form.domains ? form.domains.split(",").map((d) => d.trim()).filter(Boolean) : [],
        monthly_cost: Number(form.monthly_cost || 0), billing_cycle: form.billing_cycle,
        renewal_date: form.renewal_date || null, tracking_source: "extension",
      });
      toast.success(`${form.name} added. Assign seats via a CSV import or the directory.`);
      setOpen(false);
      onCreated();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button data-testid="add-app-btn" className="rounded-[10px] bg-nb-navy hover:opacity-90 text-nb-cream font-semibold gap-2">
          <Plus size={16} strokeWidth={1.5} /> Add tool
        </Button>
      </SheetTrigger>
      <SheetContent className="bg-nb-surface w-[420px] sm:max-w-[420px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-nb-ink">Add an AI tool</SheetTitle>
        </SheetHeader>
        <div className="space-y-4 mt-6">
          <div><Label className="text-[13px]">Name</Label><Input data-testid="app-name" value={form.name} onChange={set("name")} className="mt-1" /></div>
          <div><Label className="text-[13px]">Vendor</Label><Input data-testid="app-vendor" value={form.vendor} onChange={set("vendor")} className="mt-1" /></div>
          <div>
            <Label className="text-[13px]">Category</Label>
            <Select value={form.category} onValueChange={set("category")}>
              <SelectTrigger className="mt-1" data-testid="app-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORY_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-[13px]">URL domains (comma separated)</Label><Input data-testid="app-domains" value={form.domains} onChange={set("domains")} placeholder="harvey.ai" className="mt-1" /></div>
          <div><Label className="text-[13px]">Monthly cost (₹)</Label><Input data-testid="app-cost" type="number" value={form.monthly_cost} onChange={set("monthly_cost")} className="mt-1" /></div>
          <div>
            <Label className="text-[13px]">Billing cycle</Label>
            <Select value={form.billing_cycle} onValueChange={set("billing_cycle")}>
              <SelectTrigger className="mt-1" data-testid="app-billing"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="annual">Annual</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-[13px]">Renewal date</Label><Input data-testid="app-renewal" type="date" value={form.renewal_date} onChange={set("renewal_date")} className="mt-1" /></div>
        </div>
        <SheetFooter className="mt-8">
          <Button data-testid="app-submit" onClick={submit} className="w-full rounded-[10px] bg-nb-navy hover:opacity-90 text-nb-cream font-semibold">Add tool</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export default function Apps() {
  const { role } = useAuth();
  const { values, setFilter, clearAll } = useUrlFilters(["category", "status", "renew", "sort", "view"]);
  const sort = values.sort || "utilisation";
  const category = values.category || "all";
  const view = values.view || "grid";
  const qc = useQueryClient();
  const [archiving, setArchiving] = useState(null);
  const params = { sort, category: category === "all" ? undefined : category };
  const { data, isLoading, error, refetch } = useRangeQuery("apps", "/apps", params);

  if (error?.code === "forbidden") return <Forbidden who="Admins and IT Admins" />;
  const allApps = data?.data || [];
  let apps = values.status ? allApps.filter((a) => a.status === values.status) : allApps;
  if (values.renew) {
    if (values.renew === "overdue") {
      apps = apps.filter((a) => { const d = daysUntil(a.renewal_date); return d !== null && d <= 0; });
    } else {
      const within = Number(values.renew);
      apps = apps.filter((a) => { const d = daysUntil(a.renewal_date); return d !== null && d >= 0 && d <= within; });
    }
  }
  const underHalf = allApps.filter((a) => !a.too_early && a.utilisation < 0.5).length;

  const doArchive = async () => {
    try {
      await api.del(`/apps/${archiving.id}`);
      toast.success(`${archiving.name} archived. Past usage is kept for reports.`);
      qc.invalidateQueries({ queryKey: ["apps"] });
    } catch (e) { toast.error(e.message); }
    setArchiving(null);
  };

  const anyFilter = values.status || values.category || values.renew;

  return (
    <div>
      <PageHeader
        title="AI apps"
        summary={allApps.length ? `${underHalf} of ${allApps.length} tools are under 50% used.` : "Add the AI tools you pay for to see how each is used."}
        testid="apps-page"
        actions={<AddAppPanel onCreated={() => qc.invalidateQueries({ queryKey: ["apps"] })} />}
      />

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <FilterBar
            values={values}
            onChange={setFilter}
            onClear={clearAll}
            filters={[
              { key: "category", label: "Category", placeholder: "All categories",
                options: Object.entries(CATEGORY_LABELS).map(([v, l]) => ({ value: v, label: l })) },
              { key: "status", label: "Status", placeholder: "All statuses",
                options: [
                  { value: "High usage", label: "High usage" },
                  { value: "Moderate usage", label: "Moderate usage" },
                  { value: "Low usage", label: "Low usage" },
                  { value: "Too early to tell", label: "Too early to tell" },
                ] },
              { key: "renew", label: "Renews within", placeholder: "Any time",
                options: [
                  { value: "overdue", label: "Up until now (overdue)" },
                  { value: "30", label: "30 days" },
                  { value: "90", label: "90 days" },
                  { value: "365", label: "365 days" },
                ] },
            ]}
            sort={{ key: "sort", label: "Sort by", options: [
              { value: "utilisation", label: "Lowest use" },
              { value: "cost", label: "Highest cost" },
              { value: "renewal_date", label: "Soonest renewal" },
              { value: "name", label: "Name" },
            ] }}
          />
        </div>
        <SegmentedControl value={view} onChange={(v) => setFilter("view", v)} testid="apps-view"
          options={[{ value: "grid", label: "Grid" }, { value: "table", label: "Table" }]} />
      </div>

      {isLoading && <LoadingBlocks rows={3} />}
      {error && <ErrorState error={error} onRetry={refetch} />}
      {data && apps.length === 0 && (
        <EmptyState title={anyFilter ? "No tools match those filters." : "No AI tools added yet."} next={anyFilter ? "Try clearing a filter." : "Add your first tool to see how it is used."} action={anyFilter ? null : <AddAppPanel onCreated={refetch} />} />
      )}

      {apps.length > 0 && view === "table" && (
        <div className="mt-2" data-testid="apps-table">
          <DataTable
            getRowKey={(a) => a.id}
            onRowClick={(a) => { window.location.href = `/apps/${a.id}`; }}
            rows={apps}
            columns={[
              { key: "name", label: "Tool", always: true, render: (a) => <span>{a.name}<span className="block text-[12px] text-nb-ink2 font-normal">{a.vendor}</span></span> },
              { key: "status", label: "Status", render: (a) => <StatusBadge status={a.status} /> },
              { key: "seats_bought", label: "Seats", numeric: true },
              { key: "active_users", label: "Active", numeric: true },
              { key: "utilisation", label: "Utilisation", numeric: true, render: (a) => a.too_early ? "Too early" : pct(a.utilisation) },
              { key: "monthly_cost", label: "Monthly cost", numeric: true, render: (a) => money(a.monthly_cost) },
              { key: "cost_per_active_user", label: "Per active user", numeric: true, render: (a) => money(a.cost_per_active_user) },
              { key: "renewal_date", label: "Renews", numeric: true, sortValue: (a) => a.renewal_date || "9999", render: (a) => shortDate(a.renewal_date) },
            ]}
          />
        </div>
      )}

      {apps.length > 0 && view === "grid" && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-2">
          {apps.map((a) => (
            <div key={a.id} className="relative group" data-testid={`app-card-${a.id}`}>
              {role === "admin" && (
                <div className="absolute top-4 right-4 z-10" onClick={(e) => e.preventDefault()}>
                  <RowMenu testid={`app-menu-${a.id}`} items={[
                    { label: "Archive tool\u2026", destructive: true, testid: `archive-app-${a.id}`,
                      icon: <Archive size={15} className="mr-2" />, onSelect: () => setArchiving(a) },
                  ]} />
                </div>
              )}
              <Link to={`/apps/${a.id}`} className="block h-full">
                <Card className="p-5 h-full flex flex-col gap-4">
                  <div className="flex items-center gap-3 pr-9">
                    <ToolAvatar name={a.name} size={40} />
                    <div className="min-w-0">
                      <div className="text-[15px] font-semibold text-nb-ink leading-tight truncate">{a.name}</div>
                      <div className="text-[12px] text-nb-ink2 truncate">{a.vendor} · {CATEGORY_LABELS[a.category]}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap min-h-[28px]">
                    <StatusBadge status={a.status} />
                    {a.partial_coverage && <PartialChip />}
                    {a.over_provisioned && <span className="text-[12px] text-nb-amber">More seats than people</span>}
                  </div>

                  <div>
                    <div className="flex justify-between text-[13px] text-nb-ink2 mb-1.5">
                      <MetricTip label="Utilisation" def={METRIC_DEFS.seat_utilisation} />
                      <span className="tnum text-nb-ink">{a.too_early ? "Too early" : pct(a.utilisation)}</span>
                    </div>
                    <UtilBar value={a.utilisation} overCap={a.over_provisioned} />
                    <div className="text-[12px] text-nb-ink2 mt-1.5 tnum">{a.active_users} active of {a.seats_bought} seats</div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-[13px] pt-3 border-t border-nb-border mt-auto">
                    <div><div className="text-nb-ink2 text-[12px]">Monthly</div><div className="text-nb-ink tnum">{money(a.monthly_cost)}</div></div>
                    <div><div className="text-nb-ink2 text-[12px]">Renewal</div><div className="text-nb-ink tnum">{shortDate(a.renewal_date)}</div></div>
                    <div><div className="text-nb-ink2 text-[12px]">Tracking</div><div className="text-nb-ink">{TRACKING_LABELS[a.tracking_source]}</div></div>
                  </div>
                </Card>
              </Link>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!archiving} onOpenChange={(o) => !o && setArchiving(null)}
        title={`Archive ${archiving?.name}?`}
        description="The tool is hidden from lists and seats are released, but its past usage stays in reports so historical metrics remain correct."
        confirmLabel="Archive tool" onConfirm={doArchive}
      />
    </div>
  );
}
